import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Save, Leaf, FileText, MessageSquare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Calendar, X, Loader2, MapPin, Mail, ShieldCheck, Key, SendHorizonal, Phone, AlertTriangle, Navigation, Plus, Trash2, CalendarOff } from 'lucide-react'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Database } from '../../lib/supabase'
import { supabase } from '../../lib/supabase'
import { useCreateSubcontractor, useUpdateSubcontractor, usePriceListsByType, useSubcontractorUnavailability, useCreateUnavailability, useDeleteUnavailability, queryKeys } from '../../hooks/useSupabase'
import { useAuth } from '../../lib/auth'
import { SendSMSModal } from '../../components/SendSMSModal'
import { CommentSection } from '../../components/comments'
import { ActivityLog } from '../../components/ActivityLog'
import { logSubcontractorChange } from '../../services/activityLog'
import { AddressSearch } from '../../components/AddressSearch'

type SubcontractorBase = Database['public']['Tables']['subcontractors']['Row']
// Extend with account_activated until DB migration is applied and types regenerated
type Subcontractor = SubcontractorBase & { account_activated?: boolean }
type SubcontractorInsert = Database['public']['Tables']['subcontractors']['Insert']

const predefinedColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
]

export function SubcontractorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  useAuth()
  const isNewSubcontractor = id === 'new'
  const [loading, setLoading] = useState(!isNewSubcontractor)
  const [saving, setSaving] = useState(false)
  const [subcontractor, setSubcontractor] = useState<Subcontractor | null>(null)
  const [showSMSModal, setShowSMSModal] = useState(false)
  const createSubcontractor = useCreateSubcontractor()
  const updateSubcontractor = useUpdateSubcontractor()

  // Navigation between filtered subcontractors
  const [filteredSubcontractorIds, setFilteredSubcontractorIds] = useState<string[]>([])

  // Collapsible section states
  const [basicInfoCollapsed, setBasicInfoCollapsed] = useState(false)
  const [commentsCollapsed, setCommentsCollapsed] = useState(true)
  const [activityLogCollapsed, setActivityLogCollapsed] = useState(true)

  // Unsaved changes detection
  const [originalFormData, setOriginalFormData] = useState<SubcontractorInsert | null>(null)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  // State for invite functionality
  const [sendingInvite, setSendingInvite] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showAdminToggleModal, setShowAdminToggleModal] = useState(false)

  // Unavailability state
  const [showUnavailabilityForm, setShowUnavailabilityForm] = useState(false)
  const [unavailabilityFormData, setUnavailabilityFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  })

  const [formData, setFormData] = useState<SubcontractorInsert & { is_admin?: boolean }>({
    name: '',
    email: '',
    phone: null,
    company: null,
    location: null,
    org_number: null,
    fortnox_supplier_id: null,
    color: '#3B82F6',
    is_active: true,
    is_admin: false,
    notes: null,
    address_street: null,
    address_postal_code: null,
    address_city: null,
    address_country: 'Sverige',
    price_list_id: null,
    available_days: [true, true, true, true, true, false, false] // Mon-Fri default
  })

  // Check for unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!originalFormData) return false
    return JSON.stringify(formData) !== JSON.stringify(originalFormData)
  }

  // Load filtered subcontractor IDs from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('filtered-subcontractor-ids')
      if (saved) {
        setFilteredSubcontractorIds(JSON.parse(saved))
      }
    } catch (e) {
      console.error('Error loading filtered subcontractor IDs:', e)
    }
  }, [])

  // Calculate navigation state (with wrap-around)
  const currentIndex = id && !isNewSubcontractor ? filteredSubcontractorIds.indexOf(id) : -1
  const canNavigate = filteredSubcontractorIds.length > 1 && currentIndex >= 0
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : filteredSubcontractorIds.length - 1
  const nextIndex = currentIndex < filteredSubcontractorIds.length - 1 ? currentIndex + 1 : 0

  // Handle back/cancel button with unsaved changes confirmation
  const handleBack = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        navigate('/admin/subcontractors')
      }
    } else {
      navigate('/admin/subcontractors')
    }
  }

  // Navigate to previous subcontractor (with wrap-around)
  const navigateToPrevious = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/subcontractors/${filteredSubcontractorIds[previousIndex]}`)
  }

  // Navigate to next subcontractor (with wrap-around)
  const navigateToNext = () => {
    if (!canNavigate) return
    if (hasUnsavedChanges()) {
      if (!window.confirm('Du har osparade ändringar. Vill du verkligen lämna sidan?')) {
        return
      }
    }
    navigate(`/admin/subcontractors/${filteredSubcontractorIds[nextIndex]}`)
  }

  // Keyboard navigation with Page Up/Down
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) {
        return
      }
      if (e.key === 'PageUp') {
        e.preventDefault()
        navigateToPrevious()
      } else if (e.key === 'PageDown') {
        e.preventDefault()
        navigateToNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canNavigate, filteredSubcontractorIds, previousIndex, nextIndex])

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  // Fetch subcontractor price lists
  const { data: subcontractorPriceLists = [] } = usePriceListsByType('subcontractor')

  // Set default price list for new subcontractors
  useEffect(() => {
    if (isNewSubcontractor && subcontractorPriceLists.length > 0 && !formData.price_list_id) {
      const defaultPriceList = subcontractorPriceLists.find(pl => pl.is_default)
      if (defaultPriceList) {
        setFormData(prev => ({
          ...prev,
          price_list_id: defaultPriceList.id
        }))
      }
    }
  }, [isNewSubcontractor, subcontractorPriceLists])

  // Load subcontractor data (only if not new subcontractor)
  useEffect(() => {
    if (!id) {
      navigate('/admin/subcontractors')
      return
    }

    // If it's a new subcontractor, don't load anything
    if (isNewSubcontractor) {
      setLoading(false)
      return
    }

    const loadSubcontractor = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('subcontractors')
          .select('*')
          .eq('id', id)
          .single()

        if (error) {
          console.error('Error loading subcontractor:', error)
          toast.error('Kunde inte ladda trädgårdsmästare: ' + error.message)
          navigate('/admin/subcontractors')
          return
        }

        if (!data) {
          toast.error('Trädgårdsmästare hittades inte')
          navigate('/admin/subcontractors')
          return
        }

        setSubcontractor(data)
        const loadedFormData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company,
          location: data.location,
          org_number: data.org_number,
          fortnox_supplier_id: data.fortnox_supplier_id,
          color: data.color,
          is_active: data.is_active,
          is_admin: data.is_admin ?? false,
          notes: data.notes,
          address_street: data.address_street,
          address_postal_code: data.address_postal_code,
          address_city: data.address_city,
          address_country: data.address_country || 'Sverige',
          price_list_id: data.price_list_id || null,
          available_days: data.available_days || [true, true, true, true, true, false, false]
        }
        setFormData(loadedFormData)
        setOriginalFormData(loadedFormData)
      } catch (error) {
        console.error('Error loading subcontractor:', error)
        toast.error('Kunde inte ladda trädgårdsmästare')
        navigate('/admin/subcontractors')
      } finally {
        setLoading(false)
      }
    }

    loadSubcontractor()
  }, [id, navigate, isNewSubcontractor])

  // Check if TG has page views (means they've actually used the app = account activated)
  // Login events happen automatically when clicking invite link, so we check for page_view instead
  const { data: hasActivity = false } = useQuery<boolean>({
    queryKey: ['subcontractor-has-activity', id],
    queryFn: async () => {
      if (!id || isNewSubcontractor) return false

      const { count, error } = await supabase
        .from('tg_activity_log')
        .select('*', { count: 'exact', head: true })
        .eq('subcontractor_id', id)
        .eq('action_type', 'page_view')
        .limit(1)

      if (error) {
        console.error('Error checking activity:', error)
        return false
      }

      return (count ?? 0) > 0
    },
    enabled: !isNewSubcontractor && !!id && !!subcontractor?.user_id
  })

  // Get assignment and reporting counts for this subcontractor
  const { data: assignmentCounts = { active: 0, reporting: 0 } } = useQuery<{ active: number; reporting: number }>({
    queryKey: ['subcontractor-assignment-counts', id],
    queryFn: async () => {
      if (!id || isNewSubcontractor) return { active: 0, reporting: 0 }

      // Fetch from assignment_subcontractors with tg_status and assignment status
      const { data, error } = await supabase
        .from('assignment_subcontractors')
        .select('tg_status, assignments(status)')
        .eq('subcontractor_id', id)

      if (error) {
        console.error('Error fetching assignment counts:', error)
        return { active: 0, reporting: 0 }
      }

      const excludedStatuses = ['slutford', 'avbruten']
      const needsReportingStatuses = ['ej_rapporterad', 'komplettering']

      let activeCount = 0
      let reportingCount = 0

      data?.forEach(item => {
        const assignmentStatus = (item.assignments as any)?.status
        const tgStatus = item.tg_status

        // Count active assignments (excluding slutford and avbruten)
        if (assignmentStatus && !excludedStatuses.includes(assignmentStatus)) {
          activeCount++
        }

        // Count where TG needs to submit/complete report (only on active assignments)
        if (tgStatus && needsReportingStatuses.includes(tgStatus) &&
            assignmentStatus && !excludedStatuses.includes(assignmentStatus)) {
          reportingCount++
        }
      })

      return { active: activeCount, reporting: reportingCount }
    },
    enabled: !isNewSubcontractor && !!id
  })

  // Get SMS counts for this subcontractor (total and unread)
  const { data: smsCounts = { total: 0, unread: 0 } } = useQuery<{ total: number; unread: number }>({
    queryKey: ['subcontractor-sms-counts', id],
    queryFn: async () => {
      if (!id || isNewSubcontractor) return { total: 0, unread: 0 }

      // Get total SMS count
      const { count: totalCount, error: totalError } = await supabase
        .from('communications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_type', 'subcontractor')
        .eq('recipient_id', id)

      if (totalError) {
        console.error('Error fetching total SMS count:', totalError)
        return { total: 0, unread: 0 }
      }

      // Get unread inbound SMS count
      const { count: unreadCount, error: unreadError } = await supabase
        .from('communications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_type', 'subcontractor')
        .eq('recipient_id', id)
        .eq('direction', 'inbound')
        .is('read_at', null)

      if (unreadError) {
        console.error('Error fetching unread SMS count:', unreadError)
        return { total: totalCount ?? 0, unread: 0 }
      }

      return { total: totalCount ?? 0, unread: unreadCount ?? 0 }
    },
    enabled: !isNewSubcontractor && !!id
  })

  // Unavailability data
  const { data: unavailabilityData = [] } = useSubcontractorUnavailability(id || '')
  const createUnavailability = useCreateUnavailability()
  const deleteUnavailability = useDeleteUnavailability()

  // Handle adding unavailability
  const handleAddUnavailability = async () => {
    if (!id || !unavailabilityFormData.start_date || !unavailabilityFormData.end_date) {
      toast.error('Ange både start- och slutdatum')
      return
    }

    if (unavailabilityFormData.end_date < unavailabilityFormData.start_date) {
      toast.error('Slutdatum måste vara efter startdatum')
      return
    }

    try {
      await createUnavailability.mutateAsync({
        subcontractor_id: id,
        start_date: unavailabilityFormData.start_date,
        end_date: unavailabilityFormData.end_date,
        reason: unavailabilityFormData.reason || null
      })
      toast.success('Undantag tillagt')
      setShowUnavailabilityForm(false)
      setUnavailabilityFormData({ start_date: '', end_date: '', reason: '' })
    } catch (error) {
      console.error('Error creating unavailability:', error)
      toast.error('Kunde inte lägga till undantag')
    }
  }

  // Handle deleting unavailability
  const handleDeleteUnavailability = async (unavailabilityId: string) => {
    if (!id) return

    try {
      await deleteUnavailability.mutateAsync({ id: unavailabilityId, subcontractorId: id })
      toast.success('Undantag borttaget')
    } catch (error) {
      console.error('Error deleting unavailability:', error)
      toast.error('Kunde inte ta bort undantag')
    }
  }

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Namn och e-post är obligatoriska')
      return
    }

    try {
      setSaving(true)

      if (isNewSubcontractor) {
        // Create new subcontractor
        const result = await createSubcontractor.mutateAsync(formData)

        // Log creation
        if (result?.id) {
          await logSubcontractorChange(result.id, 'created', {
            description: `Skapade trädgårdsmästare: ${formData.name}`
          })
        }

        toast.success('Trädgårdsmästare skapad framgångsrikt')
        // Navigate to the new subcontractor's detail page
        if (result?.id) {
          navigate(`/admin/subcontractors/${result.id}`)
        }
      } else {
        // Update existing subcontractor
        if (!id) return

        // Detect what changed before updating
        const changedFields: { field: string; fieldName: string; oldValue: string; newValue: string }[] = []
        if (originalFormData) {
          if (formData.name !== originalFormData.name) {
            changedFields.push({ field: 'name', fieldName: 'namn', oldValue: originalFormData.name || '', newValue: formData.name })
          }
          if (formData.email !== originalFormData.email) {
            changedFields.push({ field: 'email', fieldName: 'e-post', oldValue: originalFormData.email || '', newValue: formData.email })
          }
          if (formData.phone !== originalFormData.phone) {
            changedFields.push({ field: 'phone', fieldName: 'telefon', oldValue: originalFormData.phone || '', newValue: formData.phone || '' })
          }
          if (formData.company !== originalFormData.company) {
            changedFields.push({ field: 'company', fieldName: 'företag', oldValue: originalFormData.company || '', newValue: formData.company || '' })
          }
          if (formData.org_number !== originalFormData.org_number) {
            changedFields.push({ field: 'org_number', fieldName: 'organisationsnummer', oldValue: originalFormData.org_number || '', newValue: formData.org_number || '' })
          }
          if (formData.fortnox_supplier_id !== originalFormData.fortnox_supplier_id) {
            changedFields.push({ field: 'fortnox_supplier_id', fieldName: 'Fortnox leverantörs-ID', oldValue: originalFormData.fortnox_supplier_id || '', newValue: formData.fortnox_supplier_id || '' })
          }
          if (formData.color !== originalFormData.color) {
            changedFields.push({ field: 'color', fieldName: 'färg', oldValue: originalFormData.color || '', newValue: formData.color || '' })
          }
          if (formData.is_active !== originalFormData.is_active) {
            changedFields.push({ field: 'is_active', fieldName: 'aktivstatus', oldValue: originalFormData.is_active ? 'Aktiv' : 'Inaktiv', newValue: formData.is_active ? 'Aktiv' : 'Inaktiv' })
          }
          if (formData.is_admin !== originalFormData.is_admin) {
            changedFields.push({ field: 'is_admin', fieldName: 'adminbehörighet', oldValue: originalFormData.is_admin ? 'Admin' : 'Ej admin', newValue: formData.is_admin ? 'Admin' : 'Ej admin' })
          }
          if (formData.address_street !== originalFormData.address_street) {
            changedFields.push({ field: 'address_street', fieldName: 'gatuadress', oldValue: originalFormData.address_street || '', newValue: formData.address_street || '' })
          }
          if (formData.address_postal_code !== originalFormData.address_postal_code) {
            changedFields.push({ field: 'address_postal_code', fieldName: 'postnummer', oldValue: originalFormData.address_postal_code || '', newValue: formData.address_postal_code || '' })
          }
          if (formData.address_city !== originalFormData.address_city) {
            changedFields.push({ field: 'address_city', fieldName: 'ort', oldValue: originalFormData.address_city || '', newValue: formData.address_city || '' })
          }
          if (formData.notes !== originalFormData.notes) {
            changedFields.push({ field: 'notes', fieldName: 'anteckningar', oldValue: originalFormData.notes || '', newValue: formData.notes || '' })
          }
          if (formData.price_list_id !== originalFormData.price_list_id) {
            const oldPriceList = subcontractorPriceLists.find(pl => pl.id === originalFormData.price_list_id)
            const newPriceList = subcontractorPriceLists.find(pl => pl.id === formData.price_list_id)
            changedFields.push({
              field: 'price_list_id',
              fieldName: 'prislista',
              oldValue: oldPriceList?.name || 'Standard',
              newValue: newPriceList?.name || 'Standard'
            })
          }
        }

        await updateSubcontractor.mutateAsync({ id, updates: formData })

        // Log each change
        for (const change of changedFields) {
          await logSubcontractorChange(id, 'field_changed', {
            fieldName: change.field,
            oldValue: change.oldValue,
            newValue: change.newValue,
            description: `Ändrade ${change.fieldName}`
          })
        }

        // Refresh activity log if changes were logged
        if (changedFields.length > 0) {
          await queryClient.invalidateQueries({ queryKey: ['activity-log', 'subcontractor', id] })
        }

        // Refresh price lists if price_list_id changed (for usage count)
        if (changedFields.some(f => f.field === 'price_list_id')) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
        }

        toast.success('Trädgårdsmästare uppdaterad framgångsrikt')
        // Reload subcontractor data
        const { data } = await supabase
          .from('subcontractors')
          .select('*')
          .eq('id', id)
          .single()

        if (data) {
          setSubcontractor(data)
          // Update original form data to match saved state
          setOriginalFormData({ ...formData })
          setLastSaved(new Date())
        }
      }
    } catch (error) {
      console.error('Error saving subcontractor:', error)
      toast.error('Kunde inte spara trädgårdsmästare')
    } finally {
      setSaving(false)
    }
  }

  // Invite new subcontractor (creates auth user + subcontractor record)
  const handleInvite = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Namn och e-post är obligatoriska')
      return
    }

    try {
      setSendingInvite(true)

      // Get the current session for the auth header
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Du måste vara inloggad för att bjuda in användare')
        return
      }

      // Call the invite-user Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          phone: formData.phone || undefined,
          company: formData.company || undefined,
          is_admin: formData.is_admin || false,
          price_list_id: formData.price_list_id || undefined,
          color: formData.color || '#10B981',
          org_number: formData.org_number || undefined,
          notes: formData.notes || undefined,
          address_street: formData.address_street || undefined,
          address_postal_code: formData.address_postal_code || undefined,
          address_city: formData.address_city || undefined,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Kunde inte bjuda in användare')
      }

      // Log creation
      if (result.subcontractor?.id) {
        await logSubcontractorChange(result.subcontractor.id, 'created', {
          description: `Bjöd in trädgårdsmästare: ${formData.name} (${formData.email})`
        })
      }

      toast.success(result.message || `Inbjudan skickad till ${formData.email}`)

      // Navigate to the new subcontractor's detail page
      if (result.subcontractor?.id) {
        navigate(`/admin/subcontractors/${result.subcontractor.id}`)
      } else {
        navigate('/admin/subcontractors')
      }
    } catch (error) {
      console.error('Error inviting subcontractor:', error)
      toast.error(error instanceof Error ? error.message : 'Kunde inte bjuda in användare')
    } finally {
      setSendingInvite(false)
    }
  }

  // Send invite to existing subcontractor (without user_id)
  const handleSendInviteToExisting = async () => {
    if (!subcontractor || subcontractor.user_id) return

    try {
      setSendingInvite(true)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Du måste vara inloggad')
        return
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: subcontractor.email,
          name: subcontractor.name,
          phone: subcontractor.phone || undefined,
          company: subcontractor.company || undefined,
          is_admin: formData.is_admin || false,
          existing_subcontractor_id: subcontractor.id, // Link to existing record
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Kunde inte skicka inbjudan')
      }

      toast.success(`Inbjudan skickad till ${subcontractor.email}`)

      // Reload subcontractor data to get updated user_id
      const { data } = await supabase
        .from('subcontractors')
        .select('*')
        .eq('id', subcontractor.id)
        .single()

      if (data) {
        setSubcontractor(data)
      }
    } catch (error) {
      console.error('Error sending invite:', error)
      toast.error(error instanceof Error ? error.message : 'Kunde inte skicka inbjudan')
    } finally {
      setSendingInvite(false)
    }
  }

  // Send password reset email (also used for account activation)
  const handlePasswordReset = async () => {
    if (!subcontractor?.email) return

    try {
      setSendingInvite(true)

      // Use Edge Function to bypass CAPTCHA requirement
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: subcontractor.email,
          redirectTo: `${window.location.origin}/login`,
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Kunde inte skicka återställningslänk')
      }

      toast.success(`Återställningslänk skickad till ${subcontractor.email}`)
    } catch (error) {
      console.error('Error sending password reset:', error)
      toast.error(error instanceof Error ? error.message : 'Kunde inte skicka återställningslänk')
    } finally {
      setSendingInvite(false)
    }
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar trädgårdsmästare...</p>
        </div>
      </div>
    )
  }

  // Only return null if it's not a new subcontractor and we don't have subcontractor data
  if (!isNewSubcontractor && !subcontractor) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header Ribbon */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {!isNewSubcontractor && subcontractor && (
                <div
                  className="w-4 h-4 rounded-full border border-nature-200 dark:border-nature-600 flex-shrink-0"
                  style={{ backgroundColor: subcontractor.color }}
                />
              )}
              {!isNewSubcontractor && id && (
                <span className="text-sm font-mono text-nature-500 dark:text-nature-400">
                  #{id.substring(0, 8)}
                </span>
              )}
              <h1 className="text-lg sm:text-xl font-bold text-nature-900 dark:text-nature-100 truncate">
                {isNewSubcontractor ? 'Ny trädgårdsmästare' : (formData.name || 'Namnlös')}
              </h1>
              {!isNewSubcontractor && (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  formData.is_active
                    ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                    : 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                }`}>
                  {formData.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              )}
              {/* Account activation status - derived from activity log */}
              {!isNewSubcontractor && subcontractor?.user_id && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  hasActivity
                    ? 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                    : 'bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                }`} title={hasActivity ? 'Användaren har loggat in' : 'Användaren har inte loggat in ännu'}>
                  {hasActivity ? (
                    <>
                      <ShieldCheck className="h-3 w-3" />
                      Konto aktiverat
                    </>
                  ) : (
                    <>
                      <Key className="h-3 w-3" />
                      Ej aktiverat
                    </>
                  )}
                </span>
              )}
            </div>
            {isNewSubcontractor ? (
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-0.5">
                Fyll i uppgifterna nedan
              </p>
            ) : formData.company && (
              <p className="text-sm text-nature-500 dark:text-nature-400 mt-0.5">{formData.company}</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            {/* Navigation between filtered subcontractors (with wrap-around) */}
            {!isNewSubcontractor && filteredSubcontractorIds.length > 1 && (
              <div className="flex items-center gap-1 border-r border-nature-200 dark:border-nature-600 pr-2 mr-1">
                <button
                  onClick={navigateToPrevious}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Föregående trädgårdsmästare (Page Up)"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-xs text-nature-500 dark:text-nature-400 min-w-[3rem] text-center">
                  {currentIndex + 1} / {filteredSubcontractorIds.length}
                </span>
                <button
                  onClick={navigateToNext}
                  className="p-1.5 rounded-md transition-colors text-nature-600 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700"
                  title="Nästa trädgårdsmästare (Page Down)"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}

            {lastSaved && !hasUnsavedChanges() && (
              <span className="text-xs text-nature-500 dark:text-nature-400">
                Sparad {format(lastSaved, 'HH:mm', { locale: sv })}
              </span>
            )}


            <button
              onClick={handleBack}
              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors w-full sm:w-auto ${
                hasUnsavedChanges()
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700'
                  : 'btn-outline'
              }`}
            >
              {hasUnsavedChanges() ? (
                <>
                  <X className="h-4 w-4" />
                  <span>Avbryt</span>
                </>
              ) : (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  <span>Tillbaka</span>
                </>
              )}
            </button>

            {isNewSubcontractor ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || createSubcontractor.isPending || sendingInvite}
                  className="btn-secondary flex items-center justify-center space-x-2 w-full sm:w-auto"
                  title="Skapa utan inloggningsuppgifter"
                >
                  {saving || createSubcontractor.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>{saving || createSubcontractor.isPending ? 'Sparar...' : 'Spara'}</span>
                </button>
                <button
                  onClick={handleInvite}
                  disabled={sendingInvite || saving || createSubcontractor.isPending}
                  className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
                  title="Skapa och skicka inbjudan via e-post"
                >
                  {sendingInvite ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  <span>{sendingInvite ? 'Skickar...' : 'Bjud in'}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || updateSubcontractor.isPending || sendingInvite}
                  className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  {saving || updateSubcontractor.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>{saving || updateSubcontractor.isPending ? 'Sparar...' : 'Spara'}</span>
                </button>
                {/* Show invite button if no user_id (TG without login) */}
                {subcontractor && !subcontractor.user_id && (
                  <button
                    onClick={handleSendInviteToExisting}
                    disabled={sendingInvite || saving}
                    className="btn-secondary flex items-center justify-center space-x-2 w-full sm:w-auto"
                    title="Skicka inbjudan för att skapa inloggning"
                  >
                    {sendingInvite ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    <span>{sendingInvite ? 'Skickar...' : 'Bjud in'}</span>
                  </button>
                )}
                {/* Show activation or password reset based on activity (has logged in) */}
                {subcontractor?.user_id && !hasActivity && (
                  <button
                    onClick={handlePasswordReset}
                    disabled={sendingInvite || saving}
                    className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700 rounded-lg transition-colors"
                    title="Användaren har inte loggat in ännu. Skicka en aktiveringslänk."
                  >
                    {sendingInvite ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <SendHorizonal className="h-4 w-4" />
                    )}
                    <span>{sendingInvite ? 'Skickar...' : 'Skicka aktivering'}</span>
                  </button>
                )}
                {subcontractor?.user_id && hasActivity && (
                  <button
                    onClick={() => setShowResetPasswordModal(true)}
                    disabled={sendingInvite || saving}
                    className="btn-secondary flex items-center justify-center gap-1.5 w-full sm:w-auto px-3 py-2"
                    title="Skicka länk för att återställa lösenord"
                  >
                    <Key className="h-4 w-4" />
                    <span className="text-sm">Återställ</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Basic Info Section - Collapsible */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
        <button
          onClick={() => setBasicInfoCollapsed(!basicInfoCollapsed)}
          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
        >
          <div className="flex items-center gap-3">
            <Leaf className="h-5 w-5 text-nature-600 dark:text-nature-400" />
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Grundinformation</h2>
          </div>
          {basicInfoCollapsed ? (
            <ChevronDown className="h-5 w-5 text-nature-400" />
          ) : (
            <ChevronUp className="h-5 w-5 text-nature-400" />
          )}
        </button>

        {!basicInfoCollapsed && (
          <div className="px-6 pb-6 border-t border-nature-200 dark:border-nature-700">
            <div className="pt-6 space-y-6">
              {/* Top section - Contact info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Namn *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-field"
                    placeholder="Ange namn"
                  />
                </div>
                <div>
                  <label className="form-label">E-post *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    placeholder="ange@email.se"
                  />
                </div>
                <div>
                  <label className="form-label">Telefon</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value || null })}
                      className="input-field flex-1"
                      placeholder="+46 70 123 45 67"
                    />
                    {formData.phone && (
                      <a
                        href={`tel:${formData.phone.replace(/\s+/g, '')}`}
                        className="flex items-center justify-center px-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                        title="Ring trädgårdsmästare"
                      >
                        <Phone className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <label className="form-label">Företag</label>
                  <input
                    type="text"
                    value={formData.company || ''}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value || null })}
                    className="input-field"
                    placeholder="Ange företagsnamn"
                  />
                </div>
                <div>
                  <label className="form-label">Organisationsnummer</label>
                  <input
                    type="text"
                    value={formData.org_number || ''}
                    onChange={(e) => setFormData({ ...formData, org_number: e.target.value || null })}
                    className="input-field"
                    placeholder="123456-7890"
                  />
                </div>
                <div>
                  <label className="form-label">Fortnox leverantörs-ID</label>
                  <input
                    type="text"
                    value={formData.fortnox_supplier_id || ''}
                    onChange={(e) => setFormData({ ...formData, fortnox_supplier_id: e.target.value || null })}
                    className="input-field"
                    placeholder="Ange Fortnox leverantörs-ID"
                  />
                </div>
                <div>
                  <label className="form-label">Prislista</label>
                  <select
                    value={formData.price_list_id || ''}
                    onChange={(e) => setFormData({ ...formData, price_list_id: e.target.value || null })}
                    className="input-field"
                  >
                    {subcontractorPriceLists.map((priceList) => (
                      <option key={priceList.id} value={priceList.id}>
                        {priceList.name}
                        {priceList.is_default ? ' (Standard)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address fields */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="form-label flex items-center gap-2 mb-0">
                    <MapPin className="h-4 w-4" />
                    <span>Adress</span>
                  </label>
                  {(formData.address_street || formData.address_city) && (
                    <button
                      type="button"
                      onClick={() => {
                        const parts = [formData.address_street, formData.address_postal_code, formData.address_city, 'Sverige'].filter(Boolean)
                        if (parts.length > 0) {
                          window.open(`https://www.google.com/maps?q=${encodeURIComponent(parts.join(', '))}`, '_blank')
                        }
                      }}
                      className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      title="Öppna i Google Maps"
                    >
                      <Navigation className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <AddressSearch
                  onSelect={(address) => {
                    setFormData({
                      ...formData,
                      address_street: address.street || null,
                      address_postal_code: address.postalCode || null,
                      address_city: address.city || null,
                      address_country: address.country || 'Sverige'
                    })
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={formData.address_street || ''}
                    onChange={(e) => setFormData({ ...formData, address_street: e.target.value || null })}
                    className="input-field md:col-span-3"
                    placeholder="Gatuadress"
                  />
                  <input
                    type="text"
                    value={formData.address_postal_code || ''}
                    onChange={(e) => setFormData({ ...formData, address_postal_code: e.target.value || null })}
                    className="input-field"
                    placeholder="Postnr"
                  />
                  <input
                    type="text"
                    value={formData.address_city || ''}
                    onChange={(e) => setFormData({ ...formData, address_city: e.target.value || null })}
                    className="input-field md:col-span-2"
                    placeholder="Ort"
                  />
                </div>
              </div>

              {/* Two-column section: Notes/Color on left, Boolean fields/links on right */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-nature-100 dark:border-nature-700">
                {/* Left column - Notes and Color */}
                <div className="space-y-4">
                  {/* Notes */}
                  <div>
                    <label className="form-label flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span>Anteckningar</span>
                    </label>
                    <textarea
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value || null })}
                      className="input-field"
                      rows={8}
                      placeholder="Lägg till anteckningar om trädgårdsmästaren..."
                    />
                  </div>

                  {/* Color picker */}
                  <div>
                    <label className="form-label">Kalenderfärg</label>
                    <div className="flex items-center space-x-3 mb-2">
                      <div
                        className="w-8 h-8 rounded-full border-2 border-nature-200 dark:border-nature-600"
                        style={{ backgroundColor: formData.color }}
                      />
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-12 h-8 border border-nature-200 dark:border-nature-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-nature-600 dark:text-nature-400">{formData.color}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {predefinedColors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-6 h-6 rounded-full border-2 transition-colors ${
                            formData.color === color ? 'border-nature-900 dark:border-nature-100' : 'border-nature-200 dark:border-nature-600'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column - Status section with toggle buttons */}
                <div className="space-y-4">
                  {/* Status toggles as pill buttons */}
                  <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4 space-y-3">
                    <label className="form-label text-xs uppercase tracking-wider text-nature-500">Status</label>
                    <div className="flex flex-wrap gap-2">
                      {/* Active/Inactive toggle */}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.is_active
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700'
                            : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 ring-1 ring-red-300 dark:ring-red-700'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${formData.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                        {formData.is_active ? 'Aktiv' : 'Inaktiv'}
                      </button>

                      {/* Admin toggle */}
                      <button
                        type="button"
                        onClick={() => setShowAdminToggleModal(true)}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          formData.is_admin
                            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700'
                            : 'bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400 ring-1 ring-nature-200 dark:ring-nature-700'
                        }`}
                      >
                        <ShieldCheck className={`h-4 w-4 ${formData.is_admin ? 'text-primary-500' : 'text-nature-400'}`} />
                        {formData.is_admin ? 'Admin' : 'Ej admin'}
                      </button>
                    </div>
                  </div>

                  {/* Availability */}
                  <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4">
                    <label className="form-label text-xs uppercase tracking-wider text-nature-500 flex items-center gap-2 mb-3">
                      <Calendar className="h-3.5 w-3.5" />
                      Tillgänglighet
                      <span className="text-nature-400 font-normal normal-case">
                        ({(formData.available_days || []).filter(Boolean).length} dagar/vecka)
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'].map((day, index) => {
                        const isAvailable = formData.available_days?.[index] ?? (index < 5)
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              const newDays = [...(formData.available_days || [true, true, true, true, true, false, false])]
                              newDays[index] = !newDays[index]
                              setFormData({ ...formData, available_days: newDays })
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                              isAvailable
                                ? 'bg-primary-500 text-white shadow-sm hover:bg-primary-600'
                                : 'bg-nature-200 dark:bg-nature-700 text-nature-500 dark:text-nature-400 hover:bg-nature-300 dark:hover:bg-nature-600'
                            }`}
                          >
                            {day}
                          </button>
                        )
                      })}
                    </div>

                    {/* Unavailability exceptions */}
                    {!isNewSubcontractor && (
                      <div className="mt-4 pt-4 border-t border-nature-200 dark:border-nature-700">
                        <div className="flex items-center justify-between mb-3">
                          <label className="form-label text-xs uppercase tracking-wider text-nature-500 flex items-center gap-2 mb-0">
                            <CalendarOff className="h-3.5 w-3.5" />
                            Undantag (ej tillgänglig)
                          </label>
                        </div>

                        {/* List of existing unavailability periods */}
                        {unavailabilityData.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {unavailabilityData.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between bg-white dark:bg-nature-800 rounded-lg px-3 py-2 ring-1 ring-nature-200 dark:ring-nature-700"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-nature-700 dark:text-nature-200">
                                    {item.start_date === item.end_date
                                      ? item.start_date
                                      : `${item.start_date} → ${item.end_date}`}
                                  </span>
                                  {item.reason && (
                                    <span className="text-xs text-nature-500 dark:text-nature-400 bg-nature-100 dark:bg-nature-700 px-2 py-0.5 rounded">
                                      {item.reason}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteUnavailability(item.id)}
                                  disabled={deleteUnavailability.isPending}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                  title="Ta bort undantag"
                                >
                                  {deleteUnavailability.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add unavailability form */}
                        {showUnavailabilityForm ? (
                          <div className="bg-white dark:bg-nature-800 rounded-lg p-3 ring-1 ring-nature-200 dark:ring-nature-700 space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-nature-500 dark:text-nature-400 mb-1 block">Från</label>
                                <input
                                  type="date"
                                  value={unavailabilityFormData.start_date}
                                  onChange={(e) => setUnavailabilityFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                  className="input-field text-sm"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-nature-500 dark:text-nature-400 mb-1 block">Till</label>
                                <input
                                  type="date"
                                  value={unavailabilityFormData.end_date}
                                  onChange={(e) => setUnavailabilityFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                  className="input-field text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-nature-500 dark:text-nature-400 mb-1 block">Anledning (valfritt)</label>
                              <input
                                type="text"
                                value={unavailabilityFormData.reason}
                                onChange={(e) => setUnavailabilityFormData(prev => ({ ...prev, reason: e.target.value }))}
                                className="input-field text-sm"
                                placeholder="T.ex. Semester, Sjuk, Kurs..."
                              />
                            </div>
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowUnavailabilityForm(false)
                                  setUnavailabilityFormData({ start_date: '', end_date: '', reason: '' })
                                }}
                                className="px-3 py-1.5 text-sm text-nature-600 dark:text-nature-400 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
                              >
                                Avbryt
                              </button>
                              <button
                                type="button"
                                onClick={handleAddUnavailability}
                                disabled={createUnavailability.isPending}
                                className="px-3 py-1.5 text-sm bg-primary-500 text-white hover:bg-primary-600 rounded-lg transition-colors flex items-center gap-1"
                              >
                                {createUnavailability.isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Plus className="h-3.5 w-3.5" />
                                )}
                                Lägg till
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowUnavailabilityForm(true)}
                            className="w-full py-2 text-sm text-nature-600 dark:text-nature-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-white dark:hover:bg-nature-800 rounded-lg border border-dashed border-nature-300 dark:border-nature-600 transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Plus className="h-4 w-4" />
                            Lägg till undantag
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Quick links - only show for existing subcontractors */}
                  {!isNewSubcontractor && subcontractor && (
                    <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4">
                      <label className="form-label text-xs uppercase tracking-wider text-nature-500 mb-3">Snabblänkar</label>
                      <div className="flex flex-col gap-2">
                        {/* Uppdrag - blue */}
                        <Link
                          to="/admin/assignments"
                          onClick={() => {
                            sessionStorage.setItem('assignmentFilters', JSON.stringify({
                              showAdvancedFilters: true,
                              dateFrom: '',
                              dateTo: '',
                              selectedStatuses: [],
                              selectedSubcontractors: [subcontractor.id],
                              selectedCustomers: [],
                              selectedCities: []
                            }))
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white dark:bg-nature-800 text-nature-700 dark:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors ring-1 ring-nature-200 dark:ring-nature-700"
                        >
                          <Calendar className="h-4 w-4 text-primary-500" />
                          Visa uppdrag
                          <span className="ml-auto flex items-center gap-2">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
                              {assignmentCounts.active}
                            </span>
                            <ChevronRight className="h-4 w-4 text-nature-400" />
                          </span>
                        </Link>
                        {/* Rapportering - purple */}
                        <Link
                          to="/admin/assignments"
                          onClick={() => {
                            sessionStorage.setItem('assignmentFilters', JSON.stringify({
                              showAdvancedFilters: true,
                              dateFrom: '',
                              dateTo: '',
                              selectedStatuses: [],
                              selectedSubcontractors: [subcontractor.id],
                              selectedTgStatuses: ['ej_rapporterad', 'komplettering'],
                              selectedCustomers: [],
                              selectedCities: []
                            }))
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white dark:bg-nature-800 text-nature-700 dark:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors ring-1 ring-nature-200 dark:ring-nature-700"
                        >
                          <FileText className="h-4 w-4 text-purple-500" />
                          Rapportering
                          <span className="ml-auto flex items-center gap-2">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                              {assignmentCounts.reporting}
                            </span>
                            <ChevronRight className="h-4 w-4 text-nature-400" />
                          </span>
                        </Link>
                        {/* SMS */}
                        <Link
                          to="/admin/communications"
                          onClick={() => {
                            sessionStorage.setItem('smsFilters', JSON.stringify({
                              recipientType: 'subcontractor',
                              recipientId: subcontractor.id,
                              recipientName: subcontractor.name
                            }))
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-white dark:bg-nature-800 text-nature-700 dark:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors ring-1 ring-nature-200 dark:ring-nature-700"
                        >
                          <MessageSquare className="h-4 w-4 text-nature-500" />
                          Visa SMS
                          <span className="ml-auto flex items-center gap-2">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-xs font-medium rounded-full bg-nature-200 dark:bg-nature-700 text-nature-600 dark:text-nature-300">
                              {smsCounts.total}
                            </span>
                            {smsCounts.unread > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white">
                                {smsCounts.unread}
                              </span>
                            )}
                            <ChevronRight className="h-4 w-4 text-nature-400" />
                          </span>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comments Section - Collapsible */}
      {!isNewSubcontractor && subcontractor && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700">
          <button
            onClick={() => setCommentsCollapsed(!commentsCollapsed)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors rounded-t-lg"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-nature-600 dark:text-nature-400" />
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">Kommentarer</h2>
            </div>
            {commentsCollapsed ? (
              <ChevronDown className="h-5 w-5 text-nature-400" />
            ) : (
              <ChevronUp className="h-5 w-5 text-nature-400" />
            )}
          </button>

          {!commentsCollapsed && (
            <div className="border-t border-nature-200 dark:border-nature-700 p-6">
              <CommentSection
                entityType="subcontractor"
                entityId={subcontractor.id}
              />
            </div>
          )}
        </div>
      )}

      {/* Aktivitetslogg - Collapsed by default, only for existing subcontractors */}
      {!isNewSubcontractor && id && (
        <ActivityLog
          subcontractorId={id}
          entityType="subcontractor"
          isCollapsed={activityLogCollapsed}
          onToggleCollapse={() => setActivityLogCollapsed(!activityLogCollapsed)}
        />
      )}

      {/* Send SMS Modal */}
      {subcontractor && (
        <SendSMSModal
          isOpen={showSMSModal}
          onClose={() => setShowSMSModal(false)}
          recipientType="subcontractor"
          recipientId={subcontractor.id}
          recipientName={subcontractor.name}
          recipientPhone={subcontractor.phone || ''}
        />
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordModal && subcontractor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Återställ lösenord?
              </h3>
            </div>

            <p className="text-sm text-nature-600 dark:text-nature-400 mb-2">
              En e-post med återställningslänk kommer skickas till:
            </p>
            <p className="text-sm font-medium text-nature-900 dark:text-nature-100 mb-4 bg-nature-100 dark:bg-nature-700 px-3 py-2 rounded">
              {subcontractor.email}
            </p>
            <p className="text-xs text-nature-500 dark:text-nature-400 mb-6">
              Trädgårdsmästaren kommer kunna sätta ett nytt lösenord via länken.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="px-4 py-2 text-sm font-medium text-nature-700 dark:text-nature-300 bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  setShowResetPasswordModal(false)
                  await handlePasswordReset()
                }}
                disabled={sendingInvite}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {sendingInvite ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Key className="h-4 w-4" />
                )}
                Skicka länk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Toggle Confirmation Modal */}
      {showAdminToggleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                formData.is_admin
                  ? 'bg-amber-100 dark:bg-amber-900/40'
                  : 'bg-primary-100 dark:bg-primary-900/40'
              }`}>
                {formData.is_admin ? (
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {formData.is_admin ? 'Ta bort adminbehörighet?' : 'Ge adminbehörighet?'}
              </h3>
            </div>

            {formData.is_admin ? (
              <>
                <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
                  <strong>{formData.name}</strong> kommer att förlora administratörsrättigheter och endast kunna se trädgårdsmästarvyn.
                </p>
                <p className="text-xs text-nature-500 dark:text-nature-400 mb-6">
                  Personen kommer inte längre kunna hantera uppdrag, kunder eller andra trädgårdsmästare.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-nature-600 dark:text-nature-400 mb-4">
                  <strong>{formData.name}</strong> kommer att få full administratörsåtkomst till systemet.
                </p>
                <p className="text-xs text-nature-500 dark:text-nature-400 mb-6">
                  Personen kommer kunna hantera uppdrag, kunder, trädgårdsmästare och alla inställningar.
                </p>
              </>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAdminToggleModal(false)}
                className="px-4 py-2 text-sm font-medium text-nature-700 dark:text-nature-300 bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600 rounded-lg transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  setFormData({ ...formData, is_admin: !formData.is_admin })
                  setShowAdminToggleModal(false)
                }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                  formData.is_admin
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                <ShieldCheck className="h-4 w-4" />
                {formData.is_admin ? 'Ta bort admin' : 'Ge admin'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
